import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import {
  api_v3_list_of_clans,
  api_v3_list_of_players,
  api_v3_get_matches_by_tournament_with_date,
  clans as staticClans,
} from "../../utils/constants";

function fmtYMD(d) {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const MATCH_FINISHED = "FINISHED";
const MATCH_REVIEWED = "REVIEWED";

const HomeClans = ({ tournamentId }) => {
  const [clans, setClans] = useState([]);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClanId, setSelectedClanId] = useState(null);

  useEffect(() => {
    if (!tournamentId) return;
    const load = async () => {
      try {
        const [clansRes, playersRes, matchesRes] = await Promise.all([
          axios.get(api_v3_list_of_clans()),
          axios.get(api_v3_list_of_players()),
          axios.get(api_v3_get_matches_by_tournament_with_date(tournamentId)),
        ]);
        if (clansRes.status === 200) {
          const filtered = clansRes.data.filter(
            (c) => c.name !== "<No tengo clan>" && c.name !== "Otro clan"
          );
          const sorted = filtered.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setClans(sorted);
        }
        if (playersRes.status === 200) {
          setPlayers(playersRes.data);
        }
        if (matchesRes.status === 200) {
          setMatches(matchesRes.data);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tournamentId]);

  const clanIdMap = useMemo(() => {
    const map = {};
    clans.forEach((apiClan) => {
      map[apiClan.clanId] = apiClan.clanId;
      const staticClan = staticClans.find((s) => s.name === apiClan.name);
      if (staticClan) {
        const hexId = staticClan.id.toString(16).padStart(12, "0");
        map[`00000000-0000-0000-0000-${hexId}`] = apiClan.clanId;
      }
    });
    return map;
  }, [clans]);

  const normalizeClanId = (id) => (id && clanIdMap[id]) || id;

  const clanPlayerIds = useMemo(() => {
    const map = {};
    players.forEach((p) => {
      const normalized = normalizeClanId(p.clanId);
      if (normalized) {
        if (!map[normalized]) map[normalized] = [];
        map[normalized].push(p.playerId);
      }
    });
    return map;
  }, [players, clanIdMap]);

  const today = new Date();
  const todayStr = fmtYMD(today);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = fmtYMD(tomorrow);

  const todayTomorrowMatches = matches.filter((m) => {
    if (!m.matchDate) return false;
    const d = fmtYMD(new Date(m.matchDate));
    return d === todayStr || d === tomorrowStr;
  });

  const filteredMatches = useMemo(() => {
    if (!selectedClanId) return [];
    const ids = new Set(clanPlayerIds[selectedClanId] || []);
    return todayTomorrowMatches.filter(
      (m) => ids.has(m.playerAid) || ids.has(m.playerBid)
    );
  }, [selectedClanId, clanPlayerIds, todayTomorrowMatches]);

  const todayMatches = useMemo(
    () => filteredMatches.filter((m) => fmtYMD(new Date(m.matchDate)) === todayStr),
    [filteredMatches, todayStr]
  );

  const tomorrowMatches = useMemo(
    () => filteredMatches.filter((m) => fmtYMD(new Date(m.matchDate)) === tomorrowStr),
    [filteredMatches, tomorrowStr]
  );

  const selectedClan = selectedClanId
    ? clans.find((c) => c.clanId === selectedClanId)
    : null;

  const handleClanClick = (clanId) => {
    setSelectedClanId((prev) => (prev === clanId ? null : clanId));
  };

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Madrid",
      });
    } catch {
      return "";
    }
  };

  const renderMatchTile = (m) => {
    const isFinished =
      m.status === MATCH_FINISHED || m.status === MATCH_REVIEWED;
    const winnerA =
      isFinished &&
      ((m.playerAresult || 0) > (m.playerBresult || 0) ||
        m.playerAaw === "AW");
    const winnerB =
      isFinished &&
      ((m.playerBresult || 0) > (m.playerAresult || 0) ||
        m.playerBaw === "AW");

    const divShort =
      m.divisionName === "Espadas Largas"
        ? "Esp. Largas"
        : m.divisionName === "Hombre de Armas"
          ? "H. de Armas"
          : m.divisionName;

    return (
      <MatchTile key={m.matchId}>
        <Link to={`/match/${m.matchId}/detail`}>
          <MatchTime>
            <Dot />
            {formatTime(m.matchDate)}
          </MatchTime>
          <Versus>
            <Team className={winnerA ? "win" : ""}>
              {m.playerAusername}
            </Team>
            {isFinished ? (
              <Score className={winnerA ? "win" : ""}>
                {m.playerAaw || m.playerAresult}
              </Score>
            ) : (
              <Vs>VS</Vs>
            )}
            <Team className={winnerB ? "win" : ""}>
              {m.playerBusername}
            </Team>
            {isFinished ? (
              <Score className={winnerB ? "win" : ""}>
                {m.playerBaw || m.playerBresult}
              </Score>
            ) : null}
          </Versus>
          {m.divisionName && <Tag>{divShort}</Tag>}
        </Link>
      </MatchTile>
    );
  };

  if (loading || !tournamentId) return null;
  if (clans.length === 0) return null;

  return (
    <Wrapper className="v-anim-fade-up v-delay-3">
      <Header>
        <SectionLabel>CLANES</SectionLabel>
        <Hint>
          {selectedClan
            ? `Partidos de ${selectedClan.name}`
            : "Selecciona un clan para ver sus partidos"}
        </Hint>
      </Header>

      <ClanRow>
        {clans.map((clan) => (
          <ClanIcon
            key={clan.clanId}
            $active={selectedClanId === clan.clanId}
            onClick={() => handleClanClick(clan.clanId)}
            title={clan.name}
          >
            <img src={clan.image} alt={clan.name} />
          </ClanIcon>
        ))}
      </ClanRow>

      {selectedClanId && (
        <MatchList>
          {filteredMatches.length === 0 ? (
            <EmptyState>No hay partidos de {selectedClan?.name} para hoy ni mañana</EmptyState>
          ) : (
            <>
              {todayMatches.length > 0 && (
                <DaySection>
                  <DayLabel>HOY</DayLabel>
                  {todayMatches.map((m) => renderMatchTile(m))}
                </DaySection>
              )}
              {tomorrowMatches.length > 0 && (
                <DaySection>
                  <DayLabel>MAÑANA</DayLabel>
                  {tomorrowMatches.map((m) => renderMatchTile(m))}
                </DaySection>
              )}
            </>
          )}
        </MatchList>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.section`
  width: 100%;
  opacity: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const SectionLabel = styled.p`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.8px;
  color: var(--v-mint);
  margin: 0;
`;

const Hint = styled.p`
  font-family: var(--v-font-ui);
  font-size: 12px;
  color: var(--v-secondary);
  margin: 0;
`;

const ClanRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
`;

const ClanIcon = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 0;
  border: 2px solid
    ${({ $active }) =>
      $active ? "var(--v-mint)" : "rgba(255, 255, 255, 0.08)"};
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: var(--v-mint);
    transform: translateY(-1px);
  }
`;

const MatchList = styled.div`
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MatchTile = styled.div`
  a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 1rem;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    text-decoration: none;
    color: inherit;
    transition: border-color var(--v-transition), background var(--v-transition);

    &:hover {
      border-color: rgba(60, 255, 208, 0.3);
      background: rgba(60, 255, 208, 0.06);
    }
  }
`;

const MatchTime = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-secondary);
  white-space: nowrap;
  min-width: 4rem;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--v-mint);
  flex-shrink: 0;
`;

const Versus = styled.span`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Team = styled.span`
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: 500;
  color: var(--v-white);
  white-space: nowrap;

  &.win {
    color: var(--v-mint);
    font-weight: 600;
  }
`;

const Vs = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-secondary);
  opacity: 0.4;
`;

const Score = styled.span`
  font-family: var(--v-font-mono);
  font-size: 13px;
  font-weight: 700;
  color: var(--v-secondary);
  min-width: 1.2rem;
  text-align: center;

  &.win {
    color: var(--v-mint);
  }
`;

const Tag = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--v-gold-bright);
  background: rgba(201, 160, 78, 0.1);
  padding: 1px 6px;
  border-radius: 8px;
  white-space: nowrap;
`;

const DaySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DayLabel = styled.p`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--v-secondary);
  margin: 0.5rem 0 0.25rem;

  &:first-of-type {
    margin-top: 0;
  }
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 13px;
  color: var(--v-secondary);
  text-align: center;
  padding: 1.5rem;
  margin: 0;
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
`;

export default HomeClans;
